import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Rocket, X, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dismissable banner shown on dashboards when setup < 100%.
 * Only fetched for admin users — gated via AuthContext to avoid 401 noise in the console.
 */
export default function SetupBanner() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const load = async () => {
      try {
        const [statusRes, dismissedRes] = await Promise.all([
          axios.get(`${API}/setup/status`, { withCredentials: true }),
          axios.get(`${API}/setup/dismissed`, { withCredentials: true }),
        ]);
        if (dismissedRes.data.dismissed) {
          setDismissed(true);
        } else {
          setData(statusRes.data);
        }
      } catch {
        // silent
      }
    };
    load();
  }, [user]);

  const dismiss = async () => {
    try {
      await axios.post(`${API}/setup/dismiss`, {}, { withCredentials: true });
      setDismissed(true);
    } catch {
      setDismissed(true);
    }
  };

  if (!user || user.role !== 'admin') return null;
  if (dismissed || !data || data.overall_progress === 100) return null;

  return (
    <div
      className="bg-gradient-to-r from-[#002FA7] to-indigo-600 text-white rounded-lg p-4 flex items-center gap-3 shadow-sm"
      data-testid="setup-banner"
    >
      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
        <Rocket className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-semibold text-sm">
          Configuration à {data.overall_progress}%
          {!data.required_ready && <span className="ml-2 text-amber-200 text-xs">· {data.required_progress} requis</span>}
        </div>
        <div className="text-xs text-white/80 truncate">
          Activez les intégrations pour débloquer toutes les fonctionnalités.
        </div>
      </div>
      <Link
        to="/setup"
        className="inline-flex items-center gap-1 text-xs font-medium bg-white text-[#002FA7] px-3 py-1.5 rounded-md hover:bg-neutral-100 transition-colors"
        data-testid="setup-banner-cta"
      >
        Continuer <ArrowRight className="w-3.5 h-3.5" />
      </Link>
      <button
        type="button"
        onClick={dismiss}
        className="text-white/70 hover:text-white p-1"
        aria-label="Fermer"
        data-testid="setup-banner-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
